import React from "react";
import { ArrowLeft, ArrowRight, Info, Plus, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { useTranslation } from "../../../hooks/useTranslation";

const MAX_PROMPT_LENGTH = 2000;

// Template di prompt del design (frame 4018:1753); il testo vive nei file i18n
const PROMPT_TEMPLATES = [
  { id: "supporto", labelKey: "templateSupporto", textKey: "templateSupportoText" },
  { id: "ecommerce", labelKey: "templateEcommerce", textKey: "templateEcommerceText" },
  { id: "informativo", labelKey: "templateInformativo", textKey: "templateInformativoText" },
  { id: "prenotazioni", labelKey: "templatePrenotazioni", textKey: "templatePrenotazioniText" },
];

/**
 * Step 2 del wizard custom — "Imposta il tuo Bot"
 * (frame 4010:5925 + 4018:1753): prompt di sistema (personale o da
 * template), linee guida e risposte rapide suggerite in chat.
 */
const StepSettings = ({ config, updateConfig, onNext, onBack }) => {
  const { t } = useTranslation();

  const remaining = MAX_PROMPT_LENGTH - config.systemPrompt.length;

  const applyTemplate = (template) => {
    updateConfig({
      templateId: template.id,
      systemPrompt: t(`onboardingWizard.${template.textKey}`).slice(
        0,
        MAX_PROMPT_LENGTH
      ),
    });
  };

  const setQuickReply = (index, value) => {
    const next = [...config.quickReplies];
    next[index] = value;
    updateConfig({ quickReplies: next });
  };

  const removeQuickReply = (index) => {
    updateConfig({
      quickReplies: config.quickReplies.filter((_, i) => i !== index),
    });
  };

  const addQuickReply = () => {
    updateConfig({ quickReplies: [...config.quickReplies, ""] });
  };

  return (
    <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
      <CardHeader>
        <CardTitle className="text-xl">
          {t("onboardingWizard.step2Title")}
        </CardTitle>
        <CardDescription>
          {t("onboardingWizard.step2Subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <Tabs
          value={config.promptMode}
          onValueChange={(v) => updateConfig({ promptMode: v })}
        >
          <TabsList>
            <TabsTrigger value="custom">
              {t("onboardingWizard.tabCustomPrompt")}
            </TabsTrigger>
            <TabsTrigger value="template">
              {t("onboardingWizard.tabTemplatePrompt")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {config.promptMode === "template" && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("onboardingWizard.useTemplate")}
            </span>
            {PROMPT_TEMPLATES.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => applyTemplate(template)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  config.templateId === template.id
                    ? "border-foreground text-foreground"
                    : "border-border text-muted-foreground hover:border-slate-400"
                }`}
              >
                {t(`onboardingWizard.${template.labelKey}`)}
              </button>
            ))}
          </div>
        )}

        <div>
          <Label className="text-sm">
            {t("onboardingWizard.systemPromptLabel")}
          </Label>
          <Textarea
            className="mt-1.5 min-h-[140px]"
            value={config.systemPrompt}
            onChange={(e) =>
              updateConfig({
                systemPrompt: e.target.value.slice(0, MAX_PROMPT_LENGTH),
              })
            }
            placeholder={t("onboardingWizard.systemPromptPlaceholder")}
            maxLength={MAX_PROMPT_LENGTH}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {remaining}/{MAX_PROMPT_LENGTH} {t("onboardingWizard.characters")}
          </p>
        </div>

        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Info className="h-4 w-4 shrink-0" />
            {t("onboardingWizard.promptGuidelinesTitle")}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            {t("onboardingWizard.promptGuidelinesIntro")}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-relaxed text-muted-foreground">
            <li>{t("onboardingWizard.promptGuideline1")}</li>
            <li>{t("onboardingWizard.promptGuideline2")}</li>
            <li>{t("onboardingWizard.promptGuideline3")}</li>
            <li>{t("onboardingWizard.promptGuideline4")}</li>
          </ul>
        </div>

        <div>
          <Label className="text-sm">
            {t("onboardingWizard.quickRepliesLabel")}
          </Label>
          <div className="mt-2 space-y-2">
            {config.quickReplies.map((reply, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setQuickReply(index, e.target.value)}
                  placeholder={
                    index % 2 === 0
                      ? t("onboardingWizard.quickReplyPlaceholder1")
                      : t("onboardingWizard.quickReplyPlaceholder2")
                  }
                  maxLength={120}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0 text-destructive hover:bg-red-50 hover:text-destructive"
                  onClick={() => removeQuickReply(index)}
                  aria-label={t("onboardingWizard.removeQuickReply")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={addQuickReply}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t("onboardingWizard.addQuickReply")}
          </Button>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t("onboardingWizard.quickRepliesHint")}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="secondary" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("onboardingWizard.back")}
          </Button>
          <Button type="button" onClick={onNext}>
            {t("onboardingWizard.continue")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StepSettings;
